// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useRef, useEffect } from 'react';
import { formatTime, formatDateTime } from '@loa/theme';
import type { ProviderAccessPageContext } from '../types/providerAccess';
import './WizardHeader.css';

interface WizardHeaderProps {
  context: ProviderAccessPageContext;
  lastSavedAt: string | null;
  /** Override the default title. When omitted, derives from context. */
  title?: string;
}

function formatLastSaved(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return `Today ${formatTime(isoDate)}`;
  return formatDateTime(isoDate);
}

export function WizardHeader({ context, lastSavedAt, title: titleProp }: WizardHeaderProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const helpRef = useRef<HTMLDivElement>(null);
  const lastSavedText = formatLastSaved(lastSavedAt);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const title = titleProp ?? (context.clientName
    ? `Policy details for ${context.clientName}`
    : context.policyTypeName || context.brokerageName);

  return (
    <div className="wizard-header flex flex-row h-[80px] bg-white border-b border-[#E1E1E1] relative z-50">
      <div className="w-[280px] shrink-0 flex items-center justify-center">
        {context.brokerageLogoUrl && (
          <img
            src={context.brokerageLogoUrl}
            alt={`${context.brokerageName} logo`}
            className="w-[126.316px] h-[40px] shrink-0 aspect-[60/19]"
          />
        )}
      </div>
      <div className="flex flex-1 items-center justify-between px-6 py-[10px]">
        <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">{title}</div>
        <div className="flex items-center gap-6 shrink-0">
          {lastSavedText && (
            <div className="text-black font-sans text-body-03 font-normal leading-normal not-italic whitespace-nowrap">
              Last saved: {lastSavedText}
            </div>
          )}
          <div className="relative" ref={helpRef}>
            <button
              className="flex items-center justify-center w-[48px] h-[48px] rounded-[100px] bg-lilac-05 border-none cursor-pointer p-0 transition-colors hover:bg-[#E8E6FF]"
              type="button"
              aria-label="Help"
              title="Help"
              onClick={() => setHelpOpen(!helpOpen)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask id="mask0_help" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
                  <path d="M23.5 0.5V23.5H0.5V0.5H23.5Z" fill="white" stroke="white"/>
                </mask>
                <g mask="url(#mask0_help)">
                  <path d="M17 18.4297H13L8.54999 21.3897C7.88999 21.8297 7 21.3598 7 20.5598V18.4297C4 18.4297 2 16.4297 2 13.4297V7.42969C2 4.42969 4 2.42969 7 2.42969H17C20 2.42969 22 4.42969 22 7.42969V13.4297C22 16.4297 20 18.4297 17 18.4297Z" stroke="#2D0A6C" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12.0001 11.3594V11.1494C12.0001 10.4694 12.4201 10.1094 12.8401 9.8194C13.2501 9.5394 13.66 9.17941 13.66 8.51941C13.66 7.59941 12.9201 6.85938 12.0001 6.85938C11.0801 6.85938 10.3401 7.59941 10.3401 8.51941" stroke="#2D0A6C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11.9955 13.75H12.0045" stroke="#2D0A6C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
              </svg>
            </button>

            {helpOpen && (
              <div className="absolute top-[calc(100%+8px)] right-[20px] bg-white rounded-lg py-md flex flex-col gap-sm z-50" style={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
                <div
                  className="px-md py-sm rounded-lg mx-md cursor-pointer hover:bg-[#F5F5F7] transition-colors whitespace-nowrap"
                  onClick={() => { setHelpOpen(false); }}
                >
                  <a href="mailto:authorisation@finertydata.com" className="text-[#2B2B2B] font-sans text-body-02 font-normal leading-normal underline !text-[#2B2B2B]">authorisation@finertydata.com</a>
                </div>
                <div
                  className="px-md py-sm rounded-lg mx-md cursor-pointer hover:bg-[#F5F5F7] transition-colors whitespace-nowrap"
                  onClick={() => { setHelpOpen(false); window.open('https://www.finertydata.com/legal/privacy/', '_blank', 'noopener,noreferrer'); }}
                >
                  <div className="text-[#2B2B2B] font-sans text-body-02 font-normal leading-normal">Privacy Policy</div>
                </div>
                <div
                  className="px-md py-sm rounded-lg mx-md cursor-pointer hover:bg-[#F5F5F7] transition-colors whitespace-nowrap"
                  onClick={() => { setHelpOpen(false); window.open('https://www.finertydata.com/legal/terms/', '_blank', 'noopener,noreferrer'); }}
                >
                  <div className="text-[#2B2B2B] font-sans text-body-02 font-normal leading-normal">Terms & Conditions</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
